import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  Package, 
  Feather, 
  Calendar, 
  Users,
  Check,
  Gift,
  Sparkles,
  Leaf,
  Snowflake,
  Sun,
  Flower2,
  Quote,
  ChevronDown,
  ChevronUp,
  Shield,
  Mail,
  Star,
  Globe,
  CreditCard,
  Lock,
  X
} from "lucide-react";
import axios from "axios";
import WaitlistForm from "./WaitlistForm";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Shopify product page URLs - customers select their Seal subscription plan on the product page
// Plans available on The Seasonal Post: Founding $69/qtr (locked for life), Quarterly $99, Annual $280 prepaid
const CHECKOUT_URLS = {
  seasonal: "https://shop.letterwriterssociety.com.au/products/the-seasonal-post",
  monthly: "https://shop.letterwriterssociety.com.au/products/the-monthly-post",
  gift: "https://shop.letterwriterssociety.com.au/products/the-seasonal-post-gift-one-box"
};

// Founding Spots Counter Component
const FoundingSpotsCounter = ({ spotsRemaining, totalSpots = 50 }) => {
  const percentage = ((totalSpots - spotsRemaining) / totalSpots) * 100;
  
  return (
    <div className="bg-lws-amber/10 border border-lws-amber/30 rounded-lg p-6 text-center">
      <p className="text-xs uppercase tracking-[0.3em] text-lws-amber font-semibold mb-2">
        Founding Spots Remaining
      </p>
      <div className="text-5xl font-serif text-lws-ink mb-3">
        {spotsRemaining} <span className="text-2xl text-lws-taupe">of {totalSpots}</span>
      </div>
      <div className="w-full h-2 bg-lws-ink/10 rounded-full overflow-hidden">
        <motion.div 
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="h-full bg-lws-amber"
        />
      </div>
      <p className="text-sm text-lws-taupe mt-3">
        {spotsRemaining === 0 
          ? "Founding rate sold out" 
          : `${totalSpots - spotsRemaining} founding correspondents joined`
        }
      </p>
    </div>
  );
};

// FAQ Accordion Item
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-lws-ink/10">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-6 flex items-center justify-between text-left"
      >
        <span className="font-medium text-lws-ink pr-8">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-lws-taupe flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-lws-taupe flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="pb-6"
        >
          <p className="text-lws-taupe leading-relaxed">{answer}</p>
        </motion.div>
      )}
    </div>
  );
};

// Pricing Card Component
const PricingCard = ({ 
  icon: Icon, 
  name, 
  price, 
  period, 
  description, 
  features, 
  ctaText, 
  ctaUrl, 
  highlighted = false,
  badge = null,
  disabled = false 
}) => (
  <div className={`relative rounded-lg p-8 ${
    highlighted 
      ? 'bg-lws-green text-white ring-4 ring-lws-green/20' 
      : 'bg-white border border-lws-ink/10'
  }`}>
    {badge && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
        <span className={`px-4 py-1 text-xs uppercase tracking-wider font-bold rounded-full ${
          highlighted ? 'bg-lws-amber text-white' : 'bg-lws-amber/10 text-lws-amber'
        }`}>
          {badge}
        </span>
      </div>
    )}
    <div className="text-center mb-6">
      <Icon className={`w-8 h-8 mx-auto mb-4 ${highlighted ? 'text-white/80' : 'text-lws-green'}`} />
      <h3 className={`text-xl font-serif mb-2 ${highlighted ? 'text-white' : 'text-lws-ink'}`}>
        {name}
      </h3>
      <div className="flex items-baseline justify-center gap-1">
        <span className={`text-4xl font-serif ${highlighted ? 'text-white' : 'text-lws-ink'}`}>
          {price}
        </span>
        {period && (
          <span className={highlighted ? 'text-white/70' : 'text-lws-taupe'}>
            {period}
          </span>
        )}
      </div>
      <p className={`text-sm mt-2 ${highlighted ? 'text-white/80' : 'text-lws-taupe'}`}>
        {description}
      </p>
    </div>
    {features && (
      <ul className="space-y-3 mb-6">
        {features.map((feature, idx) => (
          <li key={idx} className={`flex items-start gap-2 text-sm ${
            highlighted ? 'text-white/90' : 'text-lws-taupe'
          }`}>
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
              highlighted ? 'text-white' : 'text-lws-green'
            }`} />
            {feature}
          </li>
        ))}
      </ul>
    )}
    <a
      href={disabled ? "#" : ctaUrl}
      onClick={disabled ? (e) => e.preventDefault() : undefined}
      className={`block w-full py-4 text-center text-sm uppercase tracking-wider font-semibold rounded transition-colors ${
        disabled 
          ? 'bg-lws-ink/20 text-lws-taupe cursor-not-allowed'
          : highlighted
            ? 'bg-white text-lws-green hover:bg-lws-paper'
            : 'bg-lws-green text-white hover:bg-lws-green/90'
      }`}
    >
      {ctaText}
    </a>
  </div>
);

// Sticky CTA Bar Component
const StickyCTABar = ({ foundingSpots, foundingSoldOut }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling past the hero section (about 800px)
      const shouldShow = window.scrollY > 800;
      setIsVisible(shouldShow && !isDismissed);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDismissed]);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-0 left-0 right-0 z-50 bg-lws-green text-white shadow-2xl"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="hidden sm:block">
            <p className="font-serif text-lg">The Seasonal Post</p>
            {!foundingSoldOut && (
              <p className="text-sm text-white/80">{foundingSpots} founding spots remaining</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <a
            href={CHECKOUT_URLS.seasonal}
            className="px-6 py-2 bg-white text-lws-green text-sm uppercase tracking-wider font-bold hover:bg-lws-paper transition-colors whitespace-nowrap"
          >
            {foundingSoldOut ? "Subscribe — $99/qtr" : "Founding Rate — $69/qtr"}
          </a>
          <button
            onClick={() => setIsDismissed(true)}
            className="p-2 text-white/70 hover:text-white transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// International Waitlist Callout Component
const InternationalCallout = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/waitlist`, {
        email,
        firstName: "International",
        lastName: "Waitlist",
        phone: "",
        location: "International",
        formType: "international_box"
      });
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-lws-ink to-lws-ink/90 text-white py-8 px-6">
      <div className="max-w-4xl mx-auto text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Globe className="w-5 h-5 text-lws-amber" />
          <span className="text-xs uppercase tracking-[0.3em] text-lws-amber font-semibold">
            Outside Australia?
          </span>
        </div>
        
        <h3 className="text-2xl font-serif mb-3">
          International shipping is coming.
        </h3>
        <p className="text-white/80 mb-6 max-w-xl mx-auto">
          We're working on bringing The Seasonal Post to letter writers around the world. 
          Join the international waitlist to be first to know when we ship to your country.
        </p>

        {!isOpen && !submitted && (
          <button
            onClick={() => setIsOpen(true)}
            className="px-8 py-3 bg-lws-amber text-white text-sm uppercase tracking-wider font-semibold hover:bg-lws-amber/90 transition-colors"
          >
            Join International Waitlist
          </button>
        )}

        {isOpen && !submitted && (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded text-white placeholder-white/50 focus:outline-none focus:border-lws-amber"
            />
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-3 bg-lws-amber text-white text-sm uppercase tracking-wider font-semibold hover:bg-lws-amber/90 transition-colors disabled:opacity-50"
            >
              {submitting ? "..." : "Notify Me"}
            </button>
          </form>
        )}

        {submitted && (
          <div className="flex items-center justify-center gap-2 text-lws-amber">
            <Check className="w-5 h-5" />
            <span>You're on the list! We'll email you when international shipping launches.</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Trust Badges Component
const TrustBadges = () => (
  <div className="bg-lws-paper py-8 px-6 border-t border-lws-ink/10">
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-wrap items-center justify-center gap-8">
        {/* Payment Methods */}
        <div className="flex items-center gap-3">
          <CreditCard className="w-5 h-5 text-lws-taupe" />
          <div className="flex items-center gap-2">
            <img src="https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/au.svg" alt="AUD" className="h-4 w-6 object-cover rounded" />
            <span className="text-sm text-lws-taupe">Secure AUD Payment</span>
          </div>
        </div>

        {/* SSL Badge */}
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-lws-green" />
          <span className="text-sm text-lws-taupe">256-bit SSL Encrypted</span>
        </div>

        {/* Guarantee */}
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-lws-green" />
          <span className="text-sm text-lws-taupe">Letterbox Joy Guarantee</span>
        </div>

        {/* Payment Icons */}
        <div className="flex items-center gap-3">
          <svg className="h-8 w-auto text-lws-taupe" viewBox="0 0 38 24" fill="currentColor">
            <path d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3Z" opacity=".07"/>
            <path d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32" fill="#fff"/>
            <path d="M15 19c3.9 0 7-3.1 7-7s-3.1-7-7-7-7 3.1-7 7 3.1 7 7 7Z" fill="#EB001B"/>
            <path d="M23 19c3.9 0 7-3.1 7-7s-3.1-7-7-7-7 3.1-7 7 3.1 7 7 7Z" fill="#F79E1B"/>
            <path d="M19 16.2c1.4-1.2 2.3-2.9 2.3-4.7 0-1.8-.9-3.5-2.3-4.7-1.4 1.2-2.3 2.9-2.3 4.7 0 1.8.9 3.5 2.3 4.7Z" fill="#FF5F00"/>
          </svg>
          <svg className="h-8 w-auto text-lws-taupe" viewBox="0 0 38 24" fill="currentColor">
            <path d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3Z" opacity=".07"/>
            <path d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32" fill="#fff"/>
            <path d="M28.3 10.1H28c-.4 1-.7 1.5-1 3h1.9c-.3-1.5-.3-2.2-.6-3zm2.9 5.9h-1.7c-.1 0-.1 0-.2-.1l-.2-.9-.1-.2h-2.4c-.1 0-.2 0-.2.2l-.3.9c0 .1-.1.1-.1.1h-2.1l.2-.5L27 8.7c0-.5.3-.7.8-.7h1.5c.1 0 .2 0 .2.2l1.4 6.5c.1.4.2.7.2 1.1.1.1.1.1.1.2zm-13.4-.3.4-1.8c.1 0 .2.1.2.1.7.3 1.4.5 2.1.4.2 0 .5-.1.7-.2.5-.2.5-.7.1-1.1-.2-.2-.5-.3-.8-.5-.4-.2-.8-.4-1.1-.7-1.2-1-.8-2.4-.1-3.1.6-.4.9-.8 1.7-.8 1.2 0 2.5 0 3.1.2h.1c-.1.6-.2 1.1-.4 1.7-.5-.2-1-.4-1.5-.4-.3 0-.6 0-.9.1-.2 0-.3.1-.4.2-.2.2-.2.5 0 .7l.5.4c.4.2.8.4 1.1.6.5.3 1 .8 1.1 1.4.2.9-.1 1.7-.9 2.3-.5.4-.7.6-1.4.6-1.4 0-2.5.1-3.4-.2-.1.2-.1.2-.2.1zm-3.5.3c.1-.7.1-.7.2-1 .5-2.2 1-4.5 1.4-6.7.1-.2.1-.3.3-.3H18c-.2 1.2-.4 2.1-.7 3.2-.3 1.5-.6 3-1 4.5 0 .2-.1.2-.3.2M5 8.2c0-.1.2-.2.3-.2h3.4c.5 0 .9.3 1 .8l.9 4.4c0 .1 0 .1.1.2 0-.1.1-.1.1-.1l2.1-5.1c-.1-.1 0-.2.1-.2h2.1c0 .1 0 .1-.1.2l-3.1 7.3c-.1.2-.1.3-.2.4-.1.1-.3 0-.5 0H9.7c-.1 0-.2 0-.2-.2L7.9 9.5c-.2-.2-.5-.5-.9-.6-.6-.3-1.7-.5-1.9-.5L5 8.2z" fill="#142688"/>
          </svg>
          <svg className="h-6 w-auto text-lws-taupe" viewBox="0 0 165.52 105.97" fill="currentColor">
            <path d="M150.7 0H14.8C6.6 0 0 6.6 0 14.8v76.4c0 8.2 6.6 14.8 14.8 14.8h135.9c8.2 0 14.8-6.6 14.8-14.8V14.8c0-8.2-6.6-14.8-14.8-14.8z" fill="#016fd0"/>
            <path d="M22.5 53l53.1-26.4v52.8L22.5 53zm120.5 0L89.9 79.4V26.6L143 53z" fill="#fff"/>
          </svg>
        </div>
      </div>
    </div>
  </div>
);

export const SeasonalPostPage = () => {
  const [foundingSpots, setFoundingSpots] = useState(50);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFoundingSpots();
  }, []);

  const fetchFoundingSpots = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/seasonal-post/founding-spots`);
      setFoundingSpots(response.data.spots_remaining);
    } catch (error) {
      console.error("Error fetching founding spots:", error);
      setFoundingSpots(50); // Default to 50 if error
    } finally {
      setLoading(false);
    }
  };

  const faqItems = [
    {
      question: "Where do you ship?",
      answer: "Currently shipping within Australia. International shipping is being explored — join the waitlist to be first to know."
    },
    {
      question: "When do boxes ship?",
      answer: "First boxes ship September 2025. After that, on a fixed seasonal calendar — Autumn, Winter, Spring, Summer. You'll receive your ship date with your subscription confirmation, and a reminder before each season's cutoff."
    },
    {
      question: "Can I skip or pause a season?",
      answer: "Yes. Skip or pause any season with no penalty through your subscriber portal. We'd rather you pause than cancel — and most people who pause come back."
    },
    {
      question: "Can I give it as a gift?",
      answer: "Yes — a one-off gift box is available for $89 with no auto-renew. The perfect gift for someone who deserves letterbox joy."
    },
    {
      question: "What if I miss the address cutoff?",
      answer: "We'll hold your box or offer you the option to gift that season to someone else. We'll always communicate clearly before the cutoff."
    },
    {
      question: "What is the founding rate exactly?",
      answer: "$69/quarter, charged every three months, locked for the life of your subscription. It never increases — as long as you remain subscribed. First 50 subscribers only."
    },
    {
      question: "What's the annual prepay saving?",
      answer: "$280/year versus $396 if you paid quarterly — a saving of $116. The year, prepaid."
    },
    {
      question: "Do I need to be a Letter Writers Society member?",
      answer: "No — The Seasonal Post is open to everyone. Subscribers are invited into the Pen Friend tier of the LWS community as part of their subscription."
    }
  ];

  const foundingSoldOut = foundingSpots <= 0;

  return (
    <div className="bg-lws-paper" data-testid="seasonal-post-page">
      
      {/* SECTION 1 — HERO */}
      <section className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1517971071642-34a2d3ecc9cd?w=1920&h=1080&fit=crop"
            alt="Letter writing"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-lws-paper via-lws-paper/95 to-lws-paper" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-2 bg-lws-amber/10 text-lws-amber text-xs uppercase tracking-[0.3em] font-semibold mb-8">
              The Seasonal Post · First Boxes Ship September
            </span>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif leading-[1.1] text-lws-ink mb-8">
              Letterbox joy — yours when it arrives.{" "}
              <span className="italic text-lws-green">Theirs when your letter does.</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-lws-taupe font-serif max-w-3xl mx-auto mb-8 leading-relaxed">
              The Seasonal Post is a quarterly subscription box for people who believe 
              the best messages still arrive in an envelope.
            </p>
            
            <p className="text-lg text-lws-ink mb-12">
              Four times a year, everything you need to write a letter someone will keep 
              for forty years — arrives at your door.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a
                href={CHECKOUT_URLS.seasonal}
                className="px-8 py-4 bg-lws-green text-white text-sm uppercase tracking-wider font-semibold hover:bg-lws-green/90 transition-colors"
                data-testid="hero-cta-primary"
              >
                {foundingSoldOut 
                  ? "Subscribe Now — $99/quarter" 
                  : "Subscribe Now — Founding Rate $69/quarter"
                }
              </a>
              <a
                href="#pricing"
                className="px-8 py-4 border-2 border-lws-ink text-lws-ink text-sm uppercase tracking-wider font-semibold hover:bg-lws-ink hover:text-white transition-colors"
              >
                View All Plans
              </a>
            </div>

            {!loading && (
              <div className="max-w-sm mx-auto">
                <FoundingSpotsCounter spotsRemaining={foundingSpots} />
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 — THE ACHE */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-serif text-lws-ink italic mb-8">
              You remember what it felt like.
            </h2>
          </motion.div>
          
          <div className="prose prose-lg max-w-none font-serif text-lws-taupe leading-relaxed space-y-6">
            <p>
              Opening your letterbox and finding something that wasn't a bill.
            </p>
            <p>
              Something that meant someone sat down — <em>actually sat down</em> — and thought about you.
            </p>
            <p>
              We've almost forgotten that feeling exists.
            </p>
            <p>
              Now the letterbox is something to dread. Bills. Parcels from late-night online shopping 
              you barely remember ordering. The same hollow delivery joy that fades before you've closed the front door.
            </p>
            <p className="text-lws-ink">
              And somewhere in the back of your mind, a quieter thought:
            </p>
            <blockquote className="border-l-4 border-lws-green pl-6 my-8 italic text-lws-ink">
              <p>When did I last write to anyone?</p>
              <p>When did I last receive something that mattered?</p>
              <p>When I'm gone — what will be left of my story in the real world?</p>
            </blockquote>
            <p>
              You've bought beautiful stationery. It's probably still in a drawer.
            </p>
            <p>
              You've meant to write. Another year has passed.
            </p>
            <p>
              The people you love deserve better than a text. You know that.
            </p>
            <p className="text-lws-ink font-medium">
              You just never had the materials, the ritual, or the reason to actually sit down.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — THE THREE LAYERS */}
      <section className="py-24 px-6 bg-lws-paper">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif text-lws-ink mb-4">
              The Seasonal Post isn't just stationery.
            </h2>
            <p className="text-xl text-lws-taupe font-serif italic">
              It's a complete writing ritual — and the circular joy that comes from using it.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Package,
                title: "The Kit",
                emoji: "✉️",
                description: "Premium, fit-for-purpose materials — beautiful paper, matching envelopes, wax seal, seasonal accent. Everything you need to write something worth receiving."
              },
              {
                icon: Sparkles,
                title: "The Ritual",
                emoji: "🕯️",
                description: "A seasonal theme, a writing prompt, and a Set the Scene card that creates the conditions for a letter worth sending. You always know exactly what to write."
              },
              {
                icon: Users,
                title: "The Membership",
                emoji: "🐌",
                description: "Access to the Letter Writers Society — a growing circle of people who believe the best messages still arrive in an envelope. You're not just subscribing to a box. You're joining a Society."
              }
            ].map((layer, idx) => (
              <motion.div
                key={layer.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-8 rounded-lg border border-lws-ink/10 text-center"
              >
                <div className="text-4xl mb-4">{layer.emoji}</div>
                <h3 className="text-xl font-serif text-lws-ink mb-4">{layer.title}</h3>
                <p className="text-lws-taupe leading-relaxed">{layer.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — WHAT'S INSIDE */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-lws-amber font-semibold">
                What's Inside
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-lws-ink mt-4 mb-8">
                Seven things. Curated for the season. Beautiful enough to use.
              </h2>
              
              <div className="space-y-6">
                {[
                  { emoji: "📄", text: "Premium writing paper — themed, seasonal, designed to be written on and kept" },
                  { emoji: "✉️", text: "Matching envelopes — because the outside matters too" },
                  { emoji: "🕯️", text: "Wax seal + stamp — the moment of ceremony before you post it" },
                  { emoji: "🖊️", text: "The season's writing prompt card — your letter brief for the quarter. You always know what to write." },
                  { emoji: "🕯️", text: "Set the Scene card — the ritual before you begin. Phone away. Candle lit. Pen in hand." },
                  { emoji: "🌿", text: "Seasonal accent — a pressed botanical, a clipping, a small beautiful thing to enclose" },
                  { emoji: "💌", text: "A note from Frederica — connecting this season's theme to the practice of correspondence" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <span className="text-2xl">{item.emoji}</span>
                    <p className="text-lws-taupe">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1586075010923-2dd4570fb338?w=800&h=1000&fit=crop"
                alt="Stationery and writing materials"
                className="rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-lws-green text-white p-6 rounded-lg max-w-xs">
                <p className="font-serif italic">
                  "The box itself is a keepsake magnetic storage box. Designed to live on your desk. 
                  Designed to hold the letters you receive in return."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5 — SEASONAL THEMES */}
      <section className="py-24 px-6 bg-lws-paper">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif text-lws-ink mb-4">
              Four seasons. Four letters. A complete year of correspondence.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                season: "Autumn", 
                theme: "Gratitude", 
                letter: "The letter you should have written years ago",
                icon: Leaf,
                color: "bg-amber-100 text-amber-700"
              },
              { 
                season: "Winter", 
                theme: "Reflection", 
                letter: "The letter to your past or future self",
                icon: Snowflake,
                color: "bg-blue-100 text-blue-700"
              },
              { 
                season: "Spring", 
                theme: "Renewal", 
                letter: "The letter to someone you've been meaning to reach",
                icon: Flower2,
                color: "bg-green-100 text-green-700"
              },
              { 
                season: "Summer", 
                theme: "Joy", 
                letter: "The letter that celebrates what's good right now",
                icon: Sun,
                color: "bg-yellow-100 text-yellow-700"
              }
            ].map((item) => (
              <motion.div
                key={item.season}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-lg border border-lws-ink/10"
              >
                <div className={`w-12 h-12 ${item.color} rounded-full flex items-center justify-center mb-4`}>
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-serif text-lws-ink mb-1">{item.season}</h3>
                <p className="text-lws-green font-medium italic mb-3">{item.theme}</p>
                <p className="text-sm text-lws-taupe">{item.letter}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-lws-taupe mt-8 italic">
            Contents rotate and evolve each season — no two boxes are identical.
          </p>
        </div>
      </section>

      {/* SECTION 6 — PRICING */}
      <section id="pricing" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif text-lws-ink mb-4">
              Choose how you'd like to subscribe.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <PricingCard
              icon={Star}
              name="Founding Quarterly"
              price="$69"
              period="/quarter"
              description="Locked for life"
              badge="First 50 Only"
              features={[
                "Rate locked forever",
                "Name in Founding Ledger",
                "Handwritten welcome letter",
                "Exclusive founding wax seal",
                "First access every season"
              ]}
              ctaText={foundingSoldOut ? "Sold Out" : "Claim Founding Spot"}
              ctaUrl={CHECKOUT_URLS.seasonal}
              highlighted={!foundingSoldOut}
              disabled={foundingSoldOut}
            />
            
            <PricingCard
              icon={Calendar}
              name="Annual Prepay"
              price="$280"
              period="/year"
              description="Save $116 off quarterly"
              features={[
                "4 seasonal boxes",
                "Best value",
                "Skip or pause anytime",
                "LWS community access"
              ]}
              ctaText="Pay Annually"
              ctaUrl={CHECKOUT_URLS.seasonal}
            />
            
            <PricingCard
              icon={Package}
              name="Quarterly"
              price="$99"
              period="/quarter"
              description="One season at a time"
              features={[
                "Flexible subscription",
                "Skip or pause anytime",
                "LWS community access"
              ]}
              ctaText="Subscribe Quarterly"
              ctaUrl={CHECKOUT_URLS.seasonal}
            />

            <PricingCard
              icon={Mail}
              name="Monthly"
              price="$45"
              period="/month"
              description="Monthly letter ritual"
              features={[
                "Monthly themed supplies",
                "More frequent inspiration",
                "Skip or pause anytime",
                "LWS community access"
              ]}
              ctaText="Subscribe Monthly"
              ctaUrl={CHECKOUT_URLS.monthly}
            />
            
            <PricingCard
              icon={Gift}
              name="Gift Box"
              price="$89"
              period=""
              description="One box, no auto-renew"
              features={[
                "Perfect gift",
                "No subscription required",
                "Gift message included"
              ]}
              ctaText="Send as Gift"
              ctaUrl={CHECKOUT_URLS.gift}
            />
          </div>

          <p className="text-center text-lws-taupe mt-8 text-sm">
            All subscriptions include skip and pause. You're never locked in to a season you can't use. 
            But most people don't skip — because when it arrives, you remember why you subscribed.
          </p>
        </div>
      </section>

      {/* SECTION 7 — THE FOUNDING OFFER */}
      {!foundingSoldOut && (
        <section className="py-24 px-6 bg-lws-green text-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-xs uppercase tracking-[0.3em] text-white/70 font-semibold">
                Limited Offer
              </span>
              <h2 className="text-3xl md:text-4xl font-serif mt-4 mb-4">
                First 50 subscribers. Never offered again.
              </h2>
              <p className="text-xl text-white/80">
                When you join as a Founding Correspondent, you lock in $69/quarter for as long as you subscribe.
              </p>
              <p className="text-white/70 mt-4">
                Not for the first year. <span className="text-white font-semibold">Forever.</span>
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 mb-12">
              <h3 className="text-xl font-serif mb-6 text-center">Founding Correspondents also receive:</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { emoji: "📜", title: "Your name in the Founding Correspondents Ledger", desc: "The permanent record of the people who made this real. Kept by LWS forever." },
                  { emoji: "✉️", title: "A signed welcome letter from Frederica", desc: "Not a printed insert. A real, handwritten letter." },
                  { emoji: "🪡", title: "An exclusive founding wax seal", desc: "A design created for this moment only. Never sold, never reproduced." },
                  { emoji: "🎯", title: "First access to every seasonal drop", desc: "Before anyone else, every season, for as long as you subscribe." },
                  { emoji: "🗳️", title: "A vote on future seasonal themes", desc: "Your voice shapes where the Society goes." },
                  { emoji: "🐌", title: "The Founding Correspondents channel in Skool", desc: "The inner circle of the Letter Writers Society." }
                ].map((perk, idx) => (
                  <div key={idx} className="flex gap-4">
                    <span className="text-2xl">{perk.emoji}</span>
                    <div>
                      <h4 className="font-semibold">{perk.title}</h4>
                      <p className="text-white/70 text-sm">{perk.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center">
              <p className="text-white/80 italic mb-8">
                These perks exist because founding members are not just subscribers — they are the people 
                who made The Seasonal Post real. That deserves to be recognised, and kept.
              </p>
              <a
                href={CHECKOUT_URLS.seasonal}
                className="inline-block px-10 py-5 bg-white text-lws-green text-sm uppercase tracking-wider font-bold hover:bg-lws-paper transition-colors"
              >
                Claim Your Founding Spot — $69/quarter for life →
              </a>
              <div className="mt-6">
                <FoundingSpotsCounter spotsRemaining={foundingSpots} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SECTION 8 — MEMBERSHIP TIE-IN */}
      <section className="py-24 px-6 bg-lws-paper">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-lws-ink mb-4">
              The Seasonal Post and The Letter Writers Society — designed to work together.
            </h2>
          </div>

          <div className="prose prose-lg max-w-none text-lws-taupe leading-relaxed space-y-6">
            <p>
              The Seasonal Post is part of the Letter Writers Society — a community built around 
              one belief: that the best connection still happens on paper.
            </p>
            <p>
              As a subscriber, you're invited into <strong className="text-lws-ink">The Pen Friend tier</strong> of 
              the LWS community — weekly writing prompts, a monthly letter challenge, and a circle of people 
              who still believe in slow, deliberate correspondence.
            </p>
            <p>
              <strong className="text-lws-ink">Already a Correspondent?</strong> The Seasonal Post is the physical 
              anchor your practice has been missing. The seasons structure your writing. The box arrives and you 
              sit down. Your membership and your subscription work together — the community shares it, the chapters 
              celebrate it, and the box makes sure the letterbox joy keeps moving both ways.
            </p>
            <p>
              <strong className="text-lws-ink">Already a Postmaster running your own chapter?</strong> The Seasonal Post 
              gives your gatherings a shared seasonal thread — members unbox together, write together, and send together.
            </p>
          </div>

          <div className="text-center mt-12">
            <p className="text-lws-green font-serif text-xl italic">
              Three tiers. One Society. One practice. One box.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 9 — THE FOUNDER'S NOTE */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <Quote className="w-12 h-12 text-lws-green/30 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-serif text-lws-ink">
              Why I made this.
            </h2>
          </div>

          <div className="prose prose-lg max-w-none font-serif text-lws-taupe leading-relaxed space-y-6">
            <p>
              The Letter Writers Society exists because I believe we've traded depth for speed — and most of us know it.
            </p>
            <p>
              We feel it in the hollow scroll. In the text we send instead of the call we meant to make. 
              In the way a birthday message on a wall doesn't feel the same as a card in the mail.
            </p>
            <p>
              I built LWS for the people who haven't stopped feeling that. Who still believe that something 
              written by hand, sealed, stamped, and posted, carries a weight that nothing digital ever will.
            </p>
            <p>
              <strong className="text-lws-ink">The Seasonal Post is the physical expression of that belief.</strong>
            </p>
            <p>
              Four times a year, I want you to feel what it's like to find something beautiful in your letterbox. 
              And then I want you to send that feeling forward — to someone who hasn't felt it in years.
            </p>
            <p className="text-lws-ink">
              That's what this is.
            </p>
          </div>

          <div className="mt-12 text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_letter-writers/artifacts/sr2o7k85_founder-portrait.png"
              alt="Frederica Dillon"
              className="w-24 h-24 rounded-full mx-auto mb-4 object-cover object-top"
            />
            <p className="font-serif text-lws-ink">— Frederica Dillon</p>
            <p className="text-sm text-lws-taupe">Founder, Letter Writers Society</p>
          </div>
        </div>
      </section>

      {/* SECTION 10 — FAQ */}
      <section className="py-24 px-6 bg-lws-paper">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-lws-ink">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="bg-white rounded-lg border border-lws-ink/10 px-8">
            {faqItems.map((item, idx) => (
              <FAQItem key={idx} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 11 — GUARANTEE */}
      <section className="py-24 px-6 bg-lws-green text-white">
        <div className="max-w-3xl mx-auto text-center">
          <Shield className="w-16 h-16 mx-auto mb-8 text-white/80" />
          <h2 className="text-3xl md:text-4xl font-serif mb-8">
            The Letterbox Joy Guarantee.
          </h2>
          
          <div className="text-left space-y-6 text-white/90 mb-12">
            <p>Open your first Seasonal Post box.</p>
            <p>
              Let yourself feel what it's like to receive something beautiful in the mail — 
              something curated, intentional, made for this.
            </p>
            <p>Use the materials inside to write and send one letter.</p>
            <p>
              <strong className="text-white">
                If you don't feel the joy go both ways — arriving at your door, and arriving at theirs — 
                contact us and we'll refund your first box in full.
              </strong>
            </p>
            <p>
              We're not worried. The hardest part of letter writing is having the right things and a reason to begin.
            </p>
            <p className="text-white font-semibold">We've solved both.</p>
          </div>
        </div>
      </section>

      {/* SECTION 12 — FINAL CTA */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-serif text-lws-ink mb-4">
            The people you love deserve a letter.
          </h2>
          <p className="text-2xl md:text-3xl font-serif text-lws-green italic mb-12">
            You deserve the letterbox joy of knowing it arrived.
          </p>

          <p className="text-lws-taupe mb-8">
            Fifty founding spots. One seasonal ritual. A practice that could last the rest of your life.
          </p>

          <p className="text-xl font-serif text-lws-ink mb-12">
            Letterbox joy — yours when it arrives. Theirs when your letter does.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            {!foundingSoldOut && (
              <a
                href={CHECKOUT_URLS.seasonal}
                className="px-8 py-4 bg-lws-green text-white text-sm uppercase tracking-wider font-semibold hover:bg-lws-green/90 transition-colors"
              >
                Become a Founding Correspondent — $69/quarter for life →
              </a>
            )}
            <a
              href={CHECKOUT_URLS.seasonal}
              className="px-8 py-4 border-2 border-lws-ink text-lws-ink text-sm uppercase tracking-wider font-semibold hover:bg-lws-ink hover:text-white transition-colors"
            >
              Subscribe Quarterly — $99 →
            </a>
            <a
              href={CHECKOUT_URLS.seasonal}
              className="px-8 py-4 border-2 border-lws-green text-lws-green text-sm uppercase tracking-wider font-semibold hover:bg-lws-green hover:text-white transition-colors"
            >
              Pay Annually — $280, save $116 →
            </a>
          </div>

          {!foundingSoldOut && (
            <p className="text-sm text-lws-taupe">
              First 50 only · closes when the last spot is taken
            </p>
          )}

          <p className="text-sm text-lws-taupe mt-8">
            Skip or pause anytime. Founding rate locked for life. The Letterbox Joy Guarantee on your first box.
          </p>

          {!loading && (
            <div className="max-w-sm mx-auto mt-12">
              <FoundingSpotsCounter spotsRemaining={foundingSpots} />
            </div>
          )}
        </div>
      </section>

      {/* International Waitlist Callout */}
      <InternationalCallout />

      {/* Trust Badges */}
      <TrustBadges />

      {/* Waitlist Section (for pre-launch or international) */}
      <WaitlistForm 
        title="Not ready yet? Join the waitlist."
        subtitle="Be the first to know when we expand to international shipping or when founding spots reopen."
        tag="subbox"
      />

      {/* Sticky CTA Bar */}
      <AnimatePresence>
        <StickyCTABar foundingSpots={foundingSpots} foundingSoldOut={foundingSoldOut} />
      </AnimatePresence>
    </div>
  );
};

export default SeasonalPostPage;
